<div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" (mousedown)="close()">
  <div class="bg-base-100 rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] flex flex-col"
       (mousedown)="$event.stopPropagation()">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-base-200">
      <h3 class="text-lg font-bold">
        Versand-/Staging-Artikel via Excel bearbeiten
        <span class="text-sm font-normal text-base-content/60">— {{ operationIds.length }} Aufträge</span>
      </h3>
      <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square" (click)="close()">
        <fa-icon [icon]="faTimes" />
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-auto px-6 py-4">

      @if (errorMessage(); as msg) {
        <div class="bm-alert bm-alert-error bm-alert-soft mb-4">{{ msg }}</div>
      }

      <!-- Phase: Setup — Projekt + Vorlagen-Quelle waehlen, Excel exportieren -->
      @if (phase() === 'setup') {
        <div class="flex flex-col gap-4">
          <!-- Zweispaltig: Projekt links, Vorlagen-Quelle rechts.
               Auf schmalen Screens stapelt es vertikal. -->
          <div class="flex flex-col md:flex-row gap-6">
            <fieldset class="bm-fieldset md:flex-1">
              <label class="bm-fieldset-label">Projekt</label>
              <select class="bm-select w-full"
                      [ngModel]="selectedProjectId()"
                      (ngModelChange)="selectedProjectId.set($event)"
                      [disabled]="projectId !== null">
                <option [ngValue]="null">— Projekt auswählen —</option>
                @for (p of projects(); track p.id) {
                  <option [ngValue]="p.id">{{ p.name }}</option>
                }
              </select>
              @if (projects().length === 0) {
                <span class="text-xs text-base-content/60 mt-1">
                  Projektliste wird geladen…
                </span>
              }
            </fieldset>

            <fieldset class="bm-fieldset md:flex-1">
              <label class="bm-fieldset-label">Artikelvorlagen-Quelle</label>
              <div class="flex flex-col gap-2">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" class="bm-radio bm-radio-sm"
                         name="templateSource" value="project"
                         [checked]="templateSource() === 'project'"
                         (change)="templateSource.set('project')" />
                  Projekt-Vorlagen (über <code>comment2</code>)
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" class="bm-radio bm-radio-sm"
                         name="templateSource" value="customer"
                         [checked]="templateSource() === 'customer'"
                         (change)="templateSource.set('customer')" />
                  Kunden-Vorlagen (über <code>comment3</code>)
                </label>
              </div>
              <span class="text-xs text-base-content/60 mt-1">
                Vorlagen, die in den Aufträgen bereits verwendet werden, werden zur Vollständigkeit
                ergänzt — auch wenn sie nicht in der Quelle stehen.
              </span>
            </fieldset>
          </div>

          <div class="flex justify-end gap-2 pt-4 border-t border-base-200">
            <button class="bm-btn bm-btn-ghost" (click)="close()">Abbrechen</button>
            <button class="bm-btn bm-btn-primary"
                    [disabled]="!canExport()"
                    (click)="onExport()">
              <fa-icon [icon]="faExcel" />
              Excel exportieren
            </button>
          </div>
        </div>
      }

      <!-- Phase: Upload — bearbeitete Excel hochladen -->
      @if (phase() === 'uploading') {
        <div class="flex flex-col gap-4">
          <!-- Zweispaltig: Erklaerung links, Datei-Upload rechts.
               Auf schmalen Screens stapelt es vertikal. -->
          <div class="flex flex-col md:flex-row gap-6">
            <div class="md:flex-1 flex flex-col gap-2">
              <p class="text-sm">
                Bearbeite die Excel-Datei. Mengen-Spalten zeigen die aktuell hinterlegten
                Versandartikel-Mengen — passe sie nach Bedarf an. Du kannst hinten weitere
                Freitext-Spalten anhängen, die werden beim Import als neue
                Freitext-Versandartikel angelegt.
              </p>
              <p class="text-sm text-warning">
                Wichtig: Die erste (versteckte) Spalte enthält die Auftrags-IDs für den Round-Trip.
                Bitte nicht löschen.
              </p>
            </div>

            <fieldset class="bm-fieldset md:flex-1">
              <label class="bm-fieldset-label">Bearbeitete Excel-Datei</label>
              <input type="file" class="bm-input w-full"
                     accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                     (change)="onFileSelected($event)" />
              @if (uploadedFile()) {
                <span class="text-xs text-base-content/60 mt-1">{{ uploadedFile()!.name }}</span>
              }
            </fieldset>
          </div>

          <div class="flex justify-end gap-2 pt-4 border-t border-base-200">
            <button class="bm-btn bm-btn-ghost" (click)="phase.set('setup')">Zurück</button>
            <button class="bm-btn bm-btn-primary"
                    [disabled]="!uploadedFile() || uploadRunning()"
                    (click)="onUpload()">
              <fa-icon [icon]="faUpload" />
              {{ uploadRunning() ? 'Wird geprüft…' : 'Hochladen & prüfen' }}
            </button>
          </div>
        </div>
      }

      <!-- Phase: Review — Diff prüfen -->
      @if (phase() === 'review' && previewResult(); as preview) {
        <div class="flex flex-col gap-3 h-full">
          @if (preview.errors.length > 0) {
            <div class="bm-alert bm-alert-error bm-alert-soft">
              <ul class="list-disc list-inside">
                @for (err of preview.errors; track err) {
                  <li>{{ err }}</li>
                }
              </ul>
            </div>
          }
          @if (preview.row_errors.length > 0) {
            <div class="bm-alert bm-alert-warning bm-alert-soft">
              Zeilen-Fehler:
              <ul class="list-disc list-inside">
                @for (re of preview.row_errors; track re.row) {
                  <li>Zeile {{ re.row }}: {{ re.error }}</li>
                }
              </ul>
            </div>
          }

          <!-- Hinweistext. Der Auto-Create-Button sitzt jetzt im Spalten-Header
               „Versandkonfiguration" (ShippingConfigHeaderComponent). -->
          <div class="flex items-center gap-2">
            <span class="text-xs text-base-content/60">
              Pro Auftrag eine Zeile je Versandkonfiguration — Mengen in den Artikel-Spalten auf die Configs verteilen.
              Der nicht verteilte Rest steht in der Zeile „offen".
            </span>
          </div>

          <ag-grid-angular
            class="w-full"
            [style.height]="gridHeightStyle()"
            [domLayout]="gridDomLayout()"
            [theme]="gridTheme"
            [loadThemeGoogleFonts]="true"
            [rowData]="splitRows()"
            [columnDefs]="previewColumnDefs()"
            [defaultColDef]="previewDefaultColDef"
            [getRowId]="getRowId"
            [rowClassRules]="rowClassRules"
            [tooltipShowDelay]="200"
            [localeText]="gridLocale"
            (gridReady)="onGridReady($event)"
            (cellClicked)="onCellClicked($event)"
          />

          @if (hasParsingTakeovers()) {
            <div class="text-xs text-info bg-info/5 border border-info/20 rounded px-2 py-1.5 flex items-start gap-1.5">
              <span class="font-bold leading-tight">↻</span>
              <span>Aus der Auftragsbeschreibung übernommene Werte stehen nach dem Import im Artikelbedarf-Tab der Ticketseite zur Verfügung.</span>
            </div>
          }

          <div class="flex items-center justify-between gap-3 pt-3 border-t border-base-200">
            <div class="text-sm text-base-content/70">
              {{ changedRowsCount() }} / {{ totalRowsCount() }} Aufträge geändert
              @if (pendingShippingConfigsCount() > 0) {
                <span class="bm-badge bm-badge-xs bm-badge-info ml-2">
                  {{ pendingShippingConfigsCount() }} Versandkonfig.
                </span>
              }
              @if (rowsMissingShippingConfig().size > 0) {
                <span class="bm-badge bm-badge-xs bm-badge-error ml-2">
                  {{ rowsMissingShippingConfig().size }} ohne Versandkonfig
                </span>
              }
              @if (invalidChanges().length > 0) {
                <span class="bm-badge bm-badge-xs bm-badge-error ml-2">
                  {{ invalidChanges().length }} ungültige Werte
                </span>
              }
            </div>
            <div class="flex gap-2">
              <button class="bm-btn bm-btn-ghost" (click)="backToUpload()">Andere Datei</button>
              <button class="bm-btn bm-btn-primary"
                      [disabled]="!canApply()"
                      [title]="rowsMissingShippingConfig().size > 0 ? 'Import blockiert: ' + rowsMissingShippingConfig().size + ' Aufträge benötigen eine Versandkonfiguration' : ''"
                      (click)="onApply()">
                <fa-icon [icon]="faCheck" />
                {{ totalApplyCount() }} Änderungen importieren
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Phase: Applying / Done -->
      @if (phase() === 'applying') {
        <div class="flex flex-col items-center gap-2 py-8">
          <span class="loading loading-spinner loading-lg"></span>
          <span>Änderungen werden gespeichert…</span>
        </div>
      }

      @if (phase() === 'done' && applyResult(); as result) {
        <div class="flex flex-col gap-4">
          <div class="bm-alert" [class.bm-alert-success]="result.errors.length === 0"
                                [class.bm-alert-error]="result.errors.length > 0">
            <fa-icon [icon]="result.errors.length === 0 ? faCheck : faWarn" />
            <span>
              {{ result.created }} neu, {{ result.updated }} aktualisiert, {{ result.deleted }} gelöscht
              @if (result.errors.length > 0) {
                — {{ result.errors.length }} Fehler
              }
            </span>
          </div>

          @if (result.errors.length > 0) {
            <ul class="list-disc list-inside text-sm text-error">
              @for (err of result.errors; track err) {
                <li>{{ err }}</li>
              }
            </ul>
          }

          <div class="flex justify-end pt-4 border-t border-base-200">
            <button class="bm-btn bm-btn-primary" (click)="close()">Schließen</button>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Auto-Create-Modal: Standard-Configs fuer alle fehlenden anlegen.
       Tabelle mit Auftragstyp, Auto-Zuordnung-Beschreibung und Vorlaufzeit-
       Input pro Typ. Backend rechnet die FormData-Defaults aus, Frontend
       packt sie in pendingShippingConfigs (persistierung beim Apply-Klick). -->
  @if (showAutoCreateModal()) {
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-4"
         (mousedown)="$event.stopPropagation(); closeAutoCreateModal()">
      <div class="bg-base-100 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
           (mousedown)="$event.stopPropagation()">
        <div class="flex items-center justify-between px-6 py-4 border-b border-base-200">
          <h3 class="text-lg font-bold">
            Standard-Versandkonfigurationen anlegen
            <span class="text-sm font-normal text-base-content/60">
              — {{ rowsMissingShippingConfig().size }} fehlende Aufträge
            </span>
          </h3>
        </div>

        <div class="px-6 py-4 flex flex-col gap-4">
          <p class="text-sm text-base-content/70">
            Für alle Aufträge ohne Versandkonfiguration werden anhand der Standard-Regeln
            Default-Configs erzeugt. Die Vorlaufzeit gilt als Regel in <strong>Werktagen</strong>
            vor dem Auftragsdatum (kein festes Datum): „bis" = spätester Versandtag (Pflicht,
            0 = am Auftragstag). Ist zusätzlich „von" gesetzt, entsteht ein
            <strong>Versandfenster</strong> „Zeitraum vor Auftrag" (frühestens „von",
            spätestens „bis"); bleibt „von" leer, gilt die einfache „X Werktage vor
            Auftrag"-Regel.
          </p>

          <table class="table-auto w-full text-sm">
            <thead>
              <tr class="text-left border-b border-base-200">
                <th class="py-2 pr-4">
                  <span class="inline-flex items-center gap-1">
                    Auftragstyp
                    <!-- Hover-Definitionen der 4 Typen (mehrzeilig → eigenes Popover
                         statt data-tip). -->
                    <span class="relative inline-flex group align-middle">
                      <span class="cursor-help text-base-content/50 border border-base-content/30 rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-normal">?</span>
                      <div class="hidden group-hover:block absolute left-0 top-5 z-20 w-80 bg-base-100 border border-base-200 rounded-lg shadow-lg p-3 text-xs text-base-content/80 normal-case font-normal leading-snug">
                        <div class="font-semibold mb-1.5">Auftragstypen — automatische Zuordnung</div>
                        <ul class="flex flex-col gap-1.5">
                          <li><span class="font-semibold">Intern:</span> Auftrag eines <code>[INTERN]</code>-Projekts → Technikerversand. Lässt sich der Techniker aus dem <strong>Ticketfiliale-Namen</strong> auflösen, wird er als fester Ziel-Techniker gesetzt; sonst wird er beim Versand in der LagerApp gewählt.</li>
                          <li><span class="font-semibold">Extern (Staging):</span> externer Auftrag mit Staging-Artikeln → standardmäßig Technikerversand mit dem Auftragstechniker als Ziel.</li>
                          <li><span class="font-semibold">Extern (Materialversand):</span> Ticket-Priorität „Materialversand" → standardmäßig Filialversand.</li>
                          <li><span class="font-semibold">Extern (Standard):</span> sonstige externe Aufträge → standardmäßig Filialversand.</li>
                        </ul>
                      </div>
                    </span>
                  </span>
                </th>
                <th class="py-2 pr-4">Anzahl</th>
                <th class="py-2 pr-4">Zuordnung</th>
                <th class="py-2 pr-4 w-48">Vorlaufzeit (Werktage)<br>
                  <span class="font-normal text-base-content/60">von (frühestens) – bis (spätestens)</span></th>
              </tr>
            </thead>
            <tbody>
              <!-- Intern: Technikerversand ohne festen Ziel-Techniker -->
              <tr class="border-b border-base-200/60"
                  [class.opacity-40]="categorizedMissingRows().internal.length === 0">
                <td class="py-3 pr-4 font-semibold">Intern</td>
                <td class="py-3 pr-4">{{ categorizedMissingRows().internal.length }}</td>
                <td class="py-3 pr-4 text-base-content/80">Technikerversand</td>
                <td class="py-3 pr-4">
                  <div class="flex items-center gap-1">
                    <input type="number" min="0" class="bm-input w-full" placeholder="von"
                           [class.border-error]="leadTimeRangesInvalid()"
                           [disabled]="categorizedMissingRows().internal.length === 0"
                           [ngModel]="earliestLeadTimeInternalDays()"
                           (ngModelChange)="earliestLeadTimeInternalDays.set($event ?? null)" />
                    <span class="text-base-content/50">–</span>
                    <input type="number" min="0" class="bm-input w-full" placeholder="bis"
                           [disabled]="categorizedMissingRows().internal.length === 0"
                           [ngModel]="leadTimeInternalDays()"
                           (ngModelChange)="leadTimeInternalDays.set($event)" />
                  </div>
                </td>
              </tr>

              <!-- Extern + Staging: Technikerversand mit Auftragstechniker -->
              <tr class="border-b border-base-200/60"
                  [class.opacity-40]="categorizedMissingRows().external_staging.length === 0">
                <td class="py-3 pr-4 font-semibold">Extern (Staging)</td>
                <td class="py-3 pr-4">{{ categorizedMissingRows().external_staging.length }}</td>
                <td class="py-3 pr-4">
                  <select class="bm-select w-full"
                          [disabled]="categorizedMissingRows().external_staging.length === 0"
                          [ngModel]="assignExternalStaging()"
                          (ngModelChange)="assignExternalStaging.set($event)">
                    <option value="technician">Technikerversand</option>
                    <option value="store">Filialversand</option>
                  </select>
                </td>
                <td class="py-3 pr-4">
                  <div class="flex items-center gap-1">
                    <input type="number" min="0" class="bm-input w-full" placeholder="von"
                           [class.border-error]="leadTimeRangesInvalid()"
                           [disabled]="categorizedMissingRows().external_staging.length === 0"
                           [ngModel]="earliestLeadTimeExternalStagingDays()"
                           (ngModelChange)="earliestLeadTimeExternalStagingDays.set($event ?? null)" />
                    <span class="text-base-content/50">–</span>
                    <input type="number" min="0" class="bm-input w-full" placeholder="bis"
                           [disabled]="categorizedMissingRows().external_staging.length === 0"
                           [ngModel]="leadTimeExternalStagingDays()"
                           (ngModelChange)="leadTimeExternalStagingDays.set($event)" />
                  </div>
                </td>
              </tr>

              <!-- Extern + Material-Priority: Filialversand, sofort -->
              <tr class="border-b border-base-200/60"
                  [class.opacity-40]="categorizedMissingRows().external_material.length === 0">
                <td class="py-3 pr-4 font-semibold">Extern (Materialversand)</td>
                <td class="py-3 pr-4">{{ categorizedMissingRows().external_material.length }}</td>
                <td class="py-3 pr-4">
                  <select class="bm-select w-full"
                          [disabled]="categorizedMissingRows().external_material.length === 0"
                          [ngModel]="assignExternalMaterial()"
                          (ngModelChange)="assignExternalMaterial.set($event)">
                    <option value="technician">Technikerversand</option>
                    <option value="store">Filialversand</option>
                  </select>
                </td>
                <td class="py-3 pr-4">
                  <div class="flex items-center gap-1">
                    <input type="number" min="0" class="bm-input w-full" placeholder="von"
                           [class.border-error]="leadTimeRangesInvalid()"
                           [disabled]="categorizedMissingRows().external_material.length === 0"
                           [ngModel]="earliestLeadTimeExternalMaterialDays()"
                           (ngModelChange)="earliestLeadTimeExternalMaterialDays.set($event ?? null)" />
                    <span class="text-base-content/50">–</span>
                    <input type="number" min="0" class="bm-input w-full" placeholder="bis"
                           [disabled]="categorizedMissingRows().external_material.length === 0"
                           [ngModel]="leadTimeExternalMaterialDays()"
                           (ngModelChange)="leadTimeExternalMaterialDays.set($event)" />
                  </div>
                </td>
              </tr>

              <!-- Extern (sonst): Filialversand mit Material-Status-Logik -->
              <tr [class.opacity-40]="categorizedMissingRows().external_other.length === 0">
                <td class="py-3 pr-4 font-semibold">Extern (Standard)</td>
                <td class="py-3 pr-4">{{ categorizedMissingRows().external_other.length }}</td>
                <td class="py-3 pr-4">
                  <select class="bm-select w-full"
                          [disabled]="categorizedMissingRows().external_other.length === 0"
                          [ngModel]="assignExternalOther()"
                          (ngModelChange)="assignExternalOther.set($event)">
                    <option value="technician">Technikerversand</option>
                    <option value="store">Filialversand</option>
                  </select>
                </td>
                <td class="py-3 pr-4">
                  <div class="flex items-center gap-1">
                    <input type="number" min="0" class="bm-input w-full" placeholder="von"
                           [class.border-error]="leadTimeRangesInvalid()"
                           [disabled]="categorizedMissingRows().external_other.length === 0"
                           [ngModel]="earliestLeadTimeExternalOtherDays()"
                           (ngModelChange)="earliestLeadTimeExternalOtherDays.set($event ?? null)" />
                    <span class="text-base-content/50">–</span>
                    <input type="number" min="0" class="bm-input w-full" placeholder="bis"
                           [disabled]="categorizedMissingRows().external_other.length === 0"
                           [ngModel]="leadTimeExternalOtherDays()"
                           (ngModelChange)="leadTimeExternalOtherDays.set($event)" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Klon-Option nur, wenn ueberhaupt Auftraege mit archivierter Config
               betroffen sind. Default an (opt-out). -->
          @if (cloneEligibleCount() > 0) {
            <label class="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" class="bm-checkbox bm-checkbox-sm mt-0.5"
                     [ngModel]="cloneArchivedConfigs()"
                     (ngModelChange)="cloneArchivedConfigs.set($event)" />
              <span class="inline-flex items-center gap-1 flex-wrap">
                <span>Zuletzt versandte Config klonen
                  <span class="text-base-content/60">({{ cloneEligibleCount() }} Aufträge betroffen)</span></span>
                <span class="relative inline-flex group align-middle">
                  <span class="cursor-help text-base-content/50 border border-base-content/30 rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-normal">?</span>
                  <div class="hidden group-hover:block absolute left-0 bottom-5 z-20 w-80 bg-base-100 border border-base-200 rounded-lg shadow-lg p-3 text-xs text-base-content/80 normal-case font-normal leading-snug">
                    Aufträge mit bereits versandter Config (z.B. Nachversand) bekommen eine Kopie der
                    zuletzt versendeten Konfiguration — unabhängig vom Auftragstyp. Abgewählt folgen sie
                    stattdessen den Standard-Typ-Regeln aus der Tabelle oben.
                  </div>
                </span>
              </span>
            </label>
          }

          @if (leadTimeRangesInvalid()) {
            <div class="bm-alert bm-alert-warning bm-alert-soft text-sm">
              „von (frühestens)" muss ≥ „bis (spätestens)" sein — mehr Werktage vor dem Auftrag = früherer Tag.
            </div>
          }

          @if (autoCreateError(); as err) {
            <div class="bm-alert bm-alert-error bm-alert-soft">{{ err }}</div>
          }
        </div>

        <div class="flex justify-end gap-2 px-6 py-4 border-t border-base-200">
          <button class="bm-btn bm-btn-ghost"
                  [disabled]="autoCreateRunning()"
                  (click)="closeAutoCreateModal()">Abbrechen</button>
          <button class="bm-btn bm-btn-primary"
                  [disabled]="autoCreateRunning() || rowsMissingShippingConfig().size === 0 || leadTimeRangesInvalid()"
                  (click)="applyAutoCreate()">
            @if (autoCreateRunning()) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Standard-Configs für {{ rowsMissingShippingConfig().size }} Aufträge übernehmen
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Versand-Config-Edit-Dialog: ueber dem Bulk-Excel-Modal (z-[1100]).
       stopPropagation auf dem Backdrop verhindert, dass der Klick zum Outer-
       Modal durchschlaegt und es ebenfalls schliesst. -->
  @if (editingShippingRow(); as shipRow) {
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-4"
         (mousedown)="$event.stopPropagation(); closeShippingConfigDialog()">
      <!-- max-w-3xl wie der Config-Dialog der Ticketseite (operation-shipping-
           matrix) — schmaler quetschen sich die von/bis-Fenster-Inputs. -->
      <div class="bg-base-100 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
           (mousedown)="$event.stopPropagation()">
        <hawk-shipping-config-form
          [initialConfig]="editingInitialConfig()"
          [context]="editingShippingContext()"
          [ticketStoreId]="shipRow.ticket_store_id"
          [ticketLat]="shipRow.ticket_lat"
          [ticketLng]="shipRow.ticket_lng"
          [referenceDate]="shipRow.operation_date_iso"
          [projectId]="shipRow.project_id"
          [operationTechnicianId]="shipRow.operation_technician_id"
          [operationTechnicianName]="shipRow.technician_name"
          heading="Versandkonfiguration — Auftrag {{ shipRow.order_nr }}"
          saveLabel="Übernehmen"
          (save)="onShippingConfigSave($event)"
          (cancel)="closeShippingConfigDialog()"
        />
      </div>
    </div>
  }

</div>
